// The mark scale switches (R1981, R1992, arcade.md §7.2,
// ui-map-interface.md §6.1.9).
//
// The mark scale is the size of the fleets and the torpedoes on the map;
// the map and every range keep their size. Two switches of the settings
// blob turn it on, one for each mode: `markScaleArcade` for the arcade page
// and `markScaleCore` for the season map. The account keeps both, so the
// choice follows the player. On draws the marks at the figure of
// `[presentation.mark_scale]`, off at their own size.
//
// Until R1992 the arcade value was a percent on the device alone, in the
// key `ap.arcade.markscale`. `moveDeviceMarkScale` moves it into the blob
// once: a percent under 100 turns the arcade switch on, and the key goes.
// The key goes through `ui/localstore.ts`, because a private window or a
// blocked store throws.

import { MARK_SCALE } from '../gen/presentation';
import { readStore, removeStore } from '../ui/localstore';
import { loadSettings, onSettingsWrite, saveSettings, type GameSettings } from '../ui/settings';

/** The two modes that keep a mark scale switch. */
export type MarkMode = 'arcade' | 'core';

/** The blob key of the switch of each mode. */
export const MARK_KEYS = { arcade: 'markScaleArcade', core: 'markScaleCore' } as const satisfies
  Record<MarkMode, keyof GameSettings>;

/** The device key of the R1981 percent, which the first read moves. */
export const DEVICE_MARK_KEY = 'ap.arcade.markscale';

/** The percent that ON draws in a mode. */
export function markPct(mode: MarkMode): number {
  return mode === 'arcade' ? MARK_SCALE.arcadePct : MARK_SCALE.corePct;
}

/** The factor the layers take for a mode under some settings: the figure
 *  of the mode while its switch is on, else 1. */
export function markFactor(mode: MarkMode, s: GameSettings): number {
  return s[MARK_KEYS[mode]] ? markPct(mode) / 100 : 1;
}

/**
 * Moves the R1981 device percent into the blob, once (R1992). A percent
 * under 100 turns `markScaleArcade` on; any other value moves nothing.
 * The device key goes in both cases, so a later read finds nothing. It
 * answers true when it wrote the blob.
 */
export function moveDeviceMarkScale(): boolean {
  const raw = readStore(DEVICE_MARK_KEY);
  if (raw === null) return false;
  removeStore(DEVICE_MARK_KEY);
  const pct = raw.trim() === '' ? NaN : Number(raw);
  if (!Number.isFinite(pct) || pct >= 100) return false;
  const s = loadSettings();
  if (s.markScaleArcade) return false;
  saveSettings({ ...s, markScaleArcade: true });
  return true;
}

/** Turns the switch of a mode over and stores the blob. It answers the
 *  settings it stored. */
export function toggleMarkScale(mode: MarkMode): GameSettings {
  const s = loadSettings();
  const key = MARK_KEYS[mode];
  const next = { ...s, [key]: !s[key] };
  saveSettings(next);
  return next;
}

/**
 * Calls `fn` with the factor of a mode after each write of the blob that
 * changes it, from any surface of the page. It returns the function that
 * removes `fn`.
 */
export function onMarkScale(mode: MarkMode, fn: (k: number) => void): () => void {
  let last = markFactor(mode, loadSettings());
  return onSettingsWrite((s) => {
    const k = markFactor(mode, s);
    if (k === last) return;
    last = k;
    fn(k);
  });
}
